/**
 * Combined Junk Hook
 *
 * Combines dust swap and burn operations into a single signing experience.
 * Users sign all transactions at once instead of separately for swap and burn.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Transaction, VersionedTransaction, Connection } from '@solana/web3.js';
import bs58 from 'bs58';

import { getConnection } from '@/lib/solana/anchorClient';
import {
  getJupiterQuotes,
  serializeInstructions,
  batchSwapResults,
  isMainnet,
  calculateEstimatedSolOutput,
  type SwappableToken,
} from '@/lib/solana/jupiter';
import { deserializeVersionedTransaction, deserializeLegacyTransaction } from '@/lib/solana/swapTransaction';
import { checkSwapOrderStatus } from '@/lib/solana/swapOrderCheck';
import * as swapApi from '@/api/swap';
import * as burnApi from '@/api/burn';
import type { BurnAsset } from '@/api/burn';
import { useAuth } from '@/hooks/useAuth';
import { useWalletAddress } from '@/hooks/useWalletAddress';
import { sumTokenAccountRentSol } from '@/utils/solana/rentEstimator';
import { isMobileWallet } from '@/lib/responsive';
import { captureException, createErrorContext } from '@/lib/sentry';
import { parseSolanaError } from '@/utils/solanaErrorParser';

// Retry configuration
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000;

// Confirmation timeout in milliseconds
const CONFIRMATION_TIMEOUT_MS = 60000;

// Batch sizes
const MAX_SWAP_TOKENS_PER_BATCH = 1;
const getBurnBatchSize = () => (isMobileWallet() ? 3 : 9);
const BATCH_DELAY_MS = 500;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Processing step for combined junk operation
 */
export type CombinedJunkStep = 'preparing' | 'signing' | 'swapping' | 'burning' | 'confirming' | 'complete';

/**
 * Progress callback for combined junk UI updates
 */
export type CombinedJunkProgressCallback = (
  step: CombinedJunkStep,
  details: {
    currentIndex?: number;
    totalCount?: number;
    currentSymbol?: string;
    attemptCount?: number;
    estimatedSolOutput?: number;
    phase?: 'swap' | 'burn';
  }
) => void;

/**
 * Input for combined junk operation
 */
export interface CombinedJunkInput {
  /** Tokens to swap via Jupiter */
  tokensToSwap: SwappableToken[];
  /** Assets to burn */
  assetsToBurn: BurnAsset[];
  /** Progress callback */
  onProgress?: CombinedJunkProgressCallback;
}

/**
 * Result of combined junk operation
 */
export interface CombinedJunkResult {
  /** All transaction signatures */
  signatures: string[];
  /** Number of tokens successfully swapped */
  swappedTokens: number;
  /** Number of assets successfully burned */
  burnedAssets: number;
  /** SOL received from Jupiter swap */
  estimatedSolFromSwap: number;
  /** SOL received from rent reclaim (swap) */
  estimatedSolFromSwapRent: number;
  /** SOL received from burn rent reclaim */
  estimatedSolFromBurn: number;
  /** Total estimated SOL received */
  estimatedSolReceived: number;
  /** Mints that couldn't be swapped */
  nonSwappableTokens: string[];
}

/**
 * Prepared transaction with metadata
 */
interface PreparedTransaction {
  type: 'swap' | 'swap_wrapup' | 'burn';
  transaction: VersionedTransaction | Transaction;
  orderId: string;
  originalSignatures: Map<string, Buffer>;
  batchIndex: number;
  batchMints?: string[];
}

/**
 * Confirm a transaction with timeout and fallback verification
 */
async function confirmTransactionWithRetry(
  connection: Connection,
  signature: string,
  blockhash: string,
  lastValidBlockHeight: number
): Promise<void> {
  console.log('[COMBINED-JUNK] Confirming transaction...');

  try {
    await Promise.race([
      connection.confirmTransaction(
        { signature, blockhash, lastValidBlockHeight },
        'confirmed'
      ),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('CONFIRMATION_TIMEOUT')), CONFIRMATION_TIMEOUT_MS)
      ),
    ]);
    console.log(`[COMBINED-JUNK] Transaction confirmed: ${signature}`);
  } catch (confirmError: any) {
    if (confirmError.message === 'CONFIRMATION_TIMEOUT') {
      console.warn('[COMBINED-JUNK] Confirmation timeout, verifying status...');

      const txInfo = await connection.getTransaction(signature, {
        commitment: 'confirmed',
        maxSupportedTransactionVersion: 0,
      });

      if (txInfo) {
        if (txInfo.meta?.err) {
          throw new Error(`Transaction failed: ${JSON.stringify(txInfo.meta.err)}`);
        }
        console.log(`[COMBINED-JUNK] Transaction verified: ${signature}`);
        return;
      }

      await sleep(2000);

      const txInfoRetry = await connection.getTransaction(signature, {
        commitment: 'confirmed',
        maxSupportedTransactionVersion: 0,
      });

      if (txInfoRetry) {
        if (txInfoRetry.meta?.err) {
          throw new Error(`Transaction failed: ${JSON.stringify(txInfoRetry.meta.err)}`);
        }
        return;
      }

      throw new Error('Transaction confirmation timeout');
    }
    throw confirmError;
  }
}

/**
 * Prepare swap transactions without signing
 */
async function prepareSwapTransactions(
  tokens: SwappableToken[],
  walletAddress: string,
  connection: Connection,
  onProgress?: CombinedJunkProgressCallback
): Promise<{
  preparedTxs: PreparedTransaction[];
  swappableCount: number;
  estimatedSolFromSwap: number;
  /** Live rent deposits of the token accounts the swap will close, in SOL */
  estimatedSolFromRent: number;
  nonSwappableMints: string[];
}> {
  if (tokens.length === 0 || !isMainnet()) {
    return {
      preparedTxs: [],
      swappableCount: 0,
      estimatedSolFromSwap: 0,
      estimatedSolFromRent: 0,
      nonSwappableMints: [],
    };
  }

  console.log('[COMBINED-JUNK] Preparing swap transactions for', tokens.length, 'tokens');
  onProgress?.('preparing', { phase: 'swap', totalCount: tokens.length });

  // Get Jupiter quotes
  const { swappable, retryable, nonSwappable } = await getJupiterQuotes(
    tokens,
    walletAddress,
    connection
  );

  console.log(`[COMBINED-JUNK] Quote results: ${swappable.length} swappable, ${retryable.length} retryable, ${nonSwappable.length} non-swappable`);

  if (retryable.length > 0) {
    throw new Error('Rate limit reached. Please wait a moment and try again.');
  }

  if (swappable.length === 0) {
    return {
      preparedTxs: [],
      swappableCount: 0,
      estimatedSolFromSwap: 0,
      estimatedSolFromRent: 0,
      nonSwappableMints: nonSwappable.map((r) => r.mint),
    };
  }

  const estimatedSolFromSwap = calculateEstimatedSolOutput(swappable);
  const lamportsByMint = new Map(tokens.map((t) => [t.mint, t.lamports]));
  const estimatedSolFromRent = sumTokenAccountRentSol(
    swappable.map((r) => ({ lamports: lamportsByMint.get(r.mint) }))
  );
  const batches = batchSwapResults(swappable, MAX_SWAP_TOKENS_PER_BATCH);
  const preparedTxs: PreparedTransaction[] = [];
  const nonSwappableMints = nonSwappable.map((r) => r.mint);

  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    console.log(`[COMBINED-JUNK] Preparing swap batch ${i + 1}/${batches.length}`);

    // Build serialized instructions for backend
    const serializedInstructions = batch.flatMap((result) =>
      serializeInstructions(result.swapInstructions!)
    );

    const addressLookupTableAddresses = [
      ...new Set(batch.flatMap((r) => r.addressLookupTableAddresses)),
    ];

    const swapRequest: swapApi.SwapOrderRequest = {
      tokenAccounts: batch.map((r) => r.tokenAccount),
      tokenSwappedAmounts: batch.map((r) => r.balance),
      addressLookupTableAccounts: addressLookupTableAddresses,
      instructions: serializedInstructions,
    };

    // Create swap order with retry
    let orderId: string | undefined;
    let retries = 0;

    while (retries <= MAX_RETRIES) {
      try {
        const orderResponse = await swapApi.createSwapOrder(swapRequest);
        if (orderResponse.code !== 0 || !orderResponse.data) {
          throw new Error(orderResponse.message || 'Failed to create swap order');
        }
        orderId = orderResponse.data;
        break;
      } catch (error: any) {
        const errorMsg = error?.message || String(error);
        const isRetryable = errorMsg.includes('busy') || errorMsg.includes('500');

        if (isRetryable && retries < MAX_RETRIES) {
          await sleep(INITIAL_RETRY_DELAY_MS * Math.pow(2, retries));
          retries++;
        } else {
          throw error;
        }
      }
    }

    if (!orderId) {
      throw new Error(`Failed to create swap order after ${MAX_RETRIES} retries`);
    }

    // Get partially signed transaction
    retries = 0;
    let serializedTx: string | undefined;

    while (retries <= MAX_RETRIES) {
      try {
        const signatureResponse = await swapApi.generateSwapSignature(orderId);
        if (signatureResponse.code !== 0 || !signatureResponse.data) {
          const errorMsg = signatureResponse.message || 'Failed to get signature';
          if (errorMsg.includes('Transaction too large')) {
            // Mark as non-swappable and continue
            nonSwappableMints.push(...batch.map((r) => r.mint));
            break;
          }
          throw new Error(errorMsg);
        }
        serializedTx = signatureResponse.data.serialized_tx;
        break;
      } catch (error: any) {
        const errorMsg = error?.message || String(error);
        const isRetryable = errorMsg.includes('500') || errorMsg.includes('busy');

        if (isRetryable && retries < MAX_RETRIES) {
          await sleep(INITIAL_RETRY_DELAY_MS * Math.pow(2, retries));
          retries++;
        } else {
          throw error;
        }
      }
    }

    if (!serializedTx) continue;

    // Deserialize and store original signatures
    const transaction = deserializeVersionedTransaction(serializedTx);
    const originalSignatures = new Map<string, Buffer>();

    for (let j = 0; j < transaction.message.staticAccountKeys.length; j++) {
      const pubkey = transaction.message.staticAccountKeys[j];
      const signature = transaction.signatures[j];
      if (signature && !signature.every((b) => b === 0)) {
        originalSignatures.set(pubkey.toString(), Buffer.from(signature));
      }
    }

    preparedTxs.push({
      type: 'swap',
      transaction,
      orderId,
      originalSignatures,
      batchIndex: i,
      batchMints: batch.map((r) => r.mint),
    });
  }

  return {
    preparedTxs,
    swappableCount: preparedTxs.length,
    estimatedSolFromSwap,
    estimatedSolFromRent,
    nonSwappableMints,
  };
}

/**
 * Prepare burn transactions without signing
 */
async function prepareBurnTransactions(
  assets: BurnAsset[],
  walletAddress: string,
  onProgress?: CombinedJunkProgressCallback
): Promise<{
  preparedTxs: PreparedTransaction[];
  orderIds: string[];
}> {
  if (assets.length === 0) {
    return { preparedTxs: [], orderIds: [] };
  }

  console.log('[COMBINED-JUNK] Preparing burn transactions for', assets.length, 'assets');
  onProgress?.('preparing', { phase: 'burn', totalCount: assets.length });

  const BURN_BATCH_SIZE = getBurnBatchSize();
  const batches: BurnAsset[][] = [];

  for (let i = 0; i < assets.length; i += BURN_BATCH_SIZE) {
    batches.push(assets.slice(i, i + BURN_BATCH_SIZE));
  }

  const preparedTxs: PreparedTransaction[] = [];
  const orderIds: string[] = [];

  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    console.log(`[COMBINED-JUNK] Preparing burn batch ${i + 1}/${batches.length}`);

    // Create order with retry
    let orderId: string | undefined;
    let retries = 0;

    while (retries <= MAX_RETRIES) {
      try {
        const orderResponse = await burnApi.createBurnOrder(batch, walletAddress);
        if (orderResponse.code !== 0 || !orderResponse.data) {
          throw new Error(orderResponse.message || 'Failed to create burn order');
        }
        orderId = orderResponse.data;
        break;
      } catch (error: any) {
        const errorMsg = error?.message || String(error);
        const isRetryable = errorMsg.includes('busy') || errorMsg.includes('500');

        if (isRetryable && retries < MAX_RETRIES) {
          await sleep(INITIAL_RETRY_DELAY_MS * Math.pow(2, retries));
          retries++;
        } else {
          throw error;
        }
      }
    }

    if (!orderId) {
      throw new Error(`Failed to create burn order after ${MAX_RETRIES} retries`);
    }

    orderIds.push(orderId);

    // Add delay between batches
    if (i < batches.length - 1) {
      await sleep(BATCH_DELAY_MS);
    }
  }

  // Get signatures just-in-time (fresher blockhashes)
  for (let i = 0; i < orderIds.length; i++) {
    const orderId = orderIds[i];
    let retries = 0;

    while (retries <= MAX_RETRIES) {
      try {
        const signatureResponse = await burnApi.generateBurnSignature(orderId);
        if (signatureResponse.code !== 0 || !signatureResponse.data) {
          throw new Error(signatureResponse.message || 'Failed to get signature');
        }

        const { serialized_tx } = signatureResponse.data;
        const transaction = deserializeLegacyTransaction(serialized_tx);

        // Store original signatures
        const originalSignatures = new Map<string, Buffer>();
        for (const sig of transaction.signatures) {
          if (sig.signature) {
            originalSignatures.set(sig.publicKey.toString(), Buffer.from(sig.signature));
          }
        }

        preparedTxs.push({
          type: 'burn',
          transaction,
          orderId,
          originalSignatures,
          batchIndex: i,
        });
        break;
      } catch (error: any) {
        const errorMsg = error?.message || String(error);
        const isRetryable = errorMsg.includes('500') || errorMsg.includes('busy');

        if (isRetryable && retries < MAX_RETRIES) {
          await sleep(INITIAL_RETRY_DELAY_MS * Math.pow(2, retries));
          retries++;
        } else {
          throw error;
        }
      }
    }
  }

  return { preparedTxs, orderIds };
}

/**
 * Sign all transactions together (single wallet popup)
 */
async function signAllTransactions(
  preparedTxs: PreparedTransaction[],
  walletProvider: any
): Promise<(VersionedTransaction | Transaction)[]> {
  if (preparedTxs.length === 0) return [];

  // Separate by type for signing
  const versionedTxs: VersionedTransaction[] = [];
  const legacyTxs: Transaction[] = [];
  const versionedIndices: number[] = [];
  const legacyIndices: number[] = [];

  preparedTxs.forEach((pt, idx) => {
    if (pt.transaction instanceof VersionedTransaction) {
      versionedTxs.push(pt.transaction);
      versionedIndices.push(idx);
    } else {
      legacyTxs.push(pt.transaction);
      legacyIndices.push(idx);
    }
  });

  console.log(`[COMBINED-JUNK] Signing ${versionedTxs.length} versioned + ${legacyTxs.length} legacy transactions`);

  // Try to sign all together if wallet supports it
  const allTxs: (VersionedTransaction | Transaction)[] = [...versionedTxs, ...legacyTxs];

  let signedTxs: (VersionedTransaction | Transaction)[];

  if (typeof walletProvider.signAllTransactions === 'function') {
    try {
      signedTxs = await walletProvider.signAllTransactions(allTxs);
    } catch (error: any) {
      // Some wallets might not support mixed types, fall back to separate signing
      console.warn('[COMBINED-JUNK] Mixed signing failed, trying separate signing:', error.message);

      const signedVersioned = versionedTxs.length > 0
        ? await walletProvider.signAllTransactions(versionedTxs)
        : [];
      const signedLegacy = legacyTxs.length > 0
        ? await walletProvider.signAllTransactions(legacyTxs)
        : [];

      signedTxs = [...signedVersioned, ...signedLegacy];
    }
  } else if (typeof walletProvider.signTransaction === 'function') {
    // Fallback to individual signing
    console.warn('[COMBINED-JUNK] signAllTransactions not available, signing individually');
    signedTxs = [];
    for (const tx of allTxs) {
      const signed = await walletProvider.signTransaction(tx);
      signedTxs.push(signed);
    }
  } else {
    throw new Error('Wallet does not support transaction signing');
  }

  // Restore original signatures (Gate Wallet fix)
  const signedVersioned = signedTxs.slice(0, versionedTxs.length) as VersionedTransaction[];
  const signedLegacy = signedTxs.slice(versionedTxs.length) as Transaction[];

  // Restore versioned transaction signatures
  for (let i = 0; i < signedVersioned.length; i++) {
    const prepared = preparedTxs[versionedIndices[i]];
    const signed = signedVersioned[i];

    for (const [pubkeyStr, originalSig] of prepared.originalSignatures) {
      const signerIndex = signed.message.staticAccountKeys.findIndex(
        (key) => key.toString() === pubkeyStr
      );

      if (signerIndex >= 0 && signerIndex < signed.signatures.length) {
        const currentSig = signed.signatures[signerIndex];
        const isCorrupted = currentSig.every((b) => b === 0) ||
          Buffer.from(currentSig).toString('hex') !== originalSig.toString('hex');

        if (isCorrupted) {
          console.log(`[COMBINED-JUNK] Restoring versioned signature for ${pubkeyStr.slice(0, 8)}...`);
          signed.signatures[signerIndex] = originalSig;
        }
      }
    }
  }

  // Restore legacy transaction signatures
  for (let i = 0; i < signedLegacy.length; i++) {
    const prepared = preparedTxs[legacyIndices[i]];
    const signed = signedLegacy[i];

    for (const [pubkeyStr, originalSig] of prepared.originalSignatures) {
      const existingSig = signed.signatures.find(
        (s) => s.publicKey.toString() === pubkeyStr
      );

      if (existingSig && existingSig.signature) {
        const currentSigHex = Buffer.from(existingSig.signature).toString('hex');
        const originalSigHex = originalSig.toString('hex');

        if (existingSig.signature.every((b) => b === 0) || currentSigHex !== originalSigHex) {
          console.log(`[COMBINED-JUNK] Restoring legacy signature for ${pubkeyStr.slice(0, 8)}...`);
          existingSig.signature = originalSig;
        }
      }
    }
  }

  // Reconstruct in original order
  const result: (VersionedTransaction | Transaction)[] = new Array(preparedTxs.length);
  for (let i = 0; i < signedVersioned.length; i++) {
    result[versionedIndices[i]] = signedVersioned[i];
  }
  for (let i = 0; i < signedLegacy.length; i++) {
    result[legacyIndices[i]] = signedLegacy[i];
  }

  return result;
}

/**
 * Send and confirm transactions in order
 */
async function sendTransactions(
  signedTxs: (VersionedTransaction | Transaction)[],
  preparedTxs: PreparedTransaction[],
  connection: Connection,
  walletProvider: any,
  onProgress?: CombinedJunkProgressCallback
): Promise<string[]> {
  const signatures: string[] = [];

  for (let i = 0; i < signedTxs.length; i++) {
    const signed = signedTxs[i];
    const prepared = preparedTxs[i];
    const isVersioned = signed instanceof VersionedTransaction;
    const phase = prepared.type === 'burn' ? 'burn' : 'swap';

    console.log(`[COMBINED-JUNK] Sending ${prepared.type} transaction ${i + 1}/${signedTxs.length}`);
    onProgress?.(phase === 'swap' ? 'swapping' : 'burning', {
      phase,
      currentIndex: i,
      totalCount: signedTxs.length,
    });

    // Check if swap order already processed
    if (prepared.type === 'swap') {
      const orderStatus = await checkSwapOrderStatus(connection, prepared.orderId);
      if (orderStatus.exists && orderStatus.isUsed) {
        console.log(`[COMBINED-JUNK] Order ${prepared.orderId} already executed, skipping`);
        signatures.push(`already-processed:${prepared.orderId}`);
        continue;
      }
    }

    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

    try {
      const signature = await connection.sendRawTransaction(signed.serialize(), {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
        maxRetries: 3,
      });

      console.log(`[COMBINED-JUNK] Transaction sent: ${signature}`);
      await confirmTransactionWithRetry(connection, signature, blockhash, lastValidBlockHeight);
      signatures.push(signature);

      // Handle swap wrapUp transaction
      if (prepared.type === 'swap') {
        try {
          const wrapUpResponse = await swapApi.generateWrapUpSignature(prepared.orderId);
          if (wrapUpResponse.code === 0 && wrapUpResponse.data) {
            const wrapUpTx = deserializeLegacyTransaction(wrapUpResponse.data.serialized_tx);

            // Sign wrapUp transaction
            if (typeof walletProvider.signTransaction !== 'function') {
              console.warn('[COMBINED-JUNK] Wallet does not support signTransaction for wrapUp');
              continue;
            }

            const signedWrapUp = await walletProvider.signTransaction(wrapUpTx);

            const wrapUpSignature = await connection.sendRawTransaction(signedWrapUp.serialize(), {
              skipPreflight: false,
              preflightCommitment: 'confirmed',
              maxRetries: 3,
            });

            const { blockhash: wrapBlockhash, lastValidBlockHeight: wrapHeight } =
              await connection.getLatestBlockhash('confirmed');
            await confirmTransactionWithRetry(connection, wrapUpSignature, wrapBlockhash, wrapHeight);
            console.log(`[COMBINED-JUNK] WrapUp confirmed: ${wrapUpSignature}`);
          }
        } catch (wrapUpError) {
          console.warn(`[COMBINED-JUNK] WrapUp failed (non-critical):`, wrapUpError);
        }
      }
    } catch (error: any) {
      const errorMessage = error?.message || String(error);
      const isAlreadyProcessed = errorMessage.includes('already been processed');

      if (isAlreadyProcessed) {
        const txSigBytes = isVersioned
          ? (signed as VersionedTransaction).signatures[0]
          : (signed as Transaction).signature;

        if (txSigBytes && !txSigBytes.every((b: number) => b === 0)) {
          const signatureBase58 = bs58.encode(txSigBytes);
          const txInfo = await connection.getTransaction(signatureBase58, {
            commitment: 'confirmed',
            maxSupportedTransactionVersion: 0,
          });

          if (txInfo && !txInfo.meta?.err) {
            console.log(`[COMBINED-JUNK] Transaction already processed: ${signatureBase58}`);
            signatures.push(signatureBase58);
            continue;
          }
        }
      }

      throw error;
    }
  }

  return signatures;
}

/**
 * Main combined junk function
 */
async function combinedJunk(
  input: CombinedJunkInput,
  walletAddress: string,
  walletProvider: any
): Promise<CombinedJunkResult> {
  const { tokensToSwap, assetsToBurn, onProgress } = input;

  console.log('[COMBINED-JUNK] Starting combined operation');
  console.log(`[COMBINED-JUNK] Tokens to swap: ${tokensToSwap.length}, Assets to burn: ${assetsToBurn.length}`);

  const connection = getConnection();

  // Step 1: Prepare all transactions
  onProgress?.('preparing', { phase: 'swap', totalCount: tokensToSwap.length + assetsToBurn.length });

  const swapResult = await prepareSwapTransactions(
    tokensToSwap,
    walletAddress,
    connection,
    onProgress
  );

  const burnResult = await prepareBurnTransactions(
    assetsToBurn,
    walletAddress,
    onProgress
  );

  const allPreparedTxs = [...swapResult.preparedTxs, ...burnResult.preparedTxs];

  if (allPreparedTxs.length === 0) {
    return {
      signatures: [],
      swappedTokens: 0,
      burnedAssets: 0,
      estimatedSolFromSwap: 0,
      estimatedSolFromSwapRent: 0,
      estimatedSolFromBurn: 0,
      estimatedSolReceived: 0,
      nonSwappableTokens: swapResult.nonSwappableMints,
    };
  }

  // Step 2: Sign all transactions together
  console.log(`[COMBINED-JUNK] Signing ${allPreparedTxs.length} transactions`);
  onProgress?.('signing', { totalCount: allPreparedTxs.length });

  const signedTxs = await signAllTransactions(allPreparedTxs, walletProvider);

  // Step 3: Send transactions in order
  const signatures = await sendTransactions(signedTxs, allPreparedTxs, connection, walletProvider, onProgress);

  // Calculate results
  const swappedCount = swapResult.preparedTxs.length;
  const burnedCount = burnResult.preparedTxs.length * getBurnBatchSize(); // Approximate
  const estimatedSolFromSwapRent = swapResult.estimatedSolFromRent;

  onProgress?.('complete', {});

  return {
    signatures,
    swappedTokens: swappedCount,
    burnedAssets: Math.min(burnedCount, assetsToBurn.length),
    estimatedSolFromSwap: swapResult.estimatedSolFromSwap,
    estimatedSolFromSwapRent,
    estimatedSolFromBurn: 0, // Will be calculated by backend
    estimatedSolReceived: swapResult.estimatedSolFromSwap + estimatedSolFromSwapRent,
    nonSwappableTokens: swapResult.nonSwappableMints,
  };
}

/**
 * Hook for combined junk operations (swap + burn in single signing)
 */
export function useCombinedJunk() {
  const { address, walletProvider } = useWalletAddress();
  const { isAuthenticated, authenticate } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CombinedJunkInput) => {
      if (!address) {
        throw new Error('Wallet not connected');
      }
      if (!walletProvider) {
        throw new Error('Wallet provider not available');
      }

      if (!isAuthenticated) {
        const authenticated = await authenticate();
        if (!authenticated) {
          throw new Error('Authentication required');
        }
      }

      return combinedJunk(input, address, walletProvider);
    },
    onSuccess: (data) => {
      console.log('[useCombinedJunk] Success:', data);

      queryClient.invalidateQueries({ queryKey: ['userProgress', address] });
      queryClient.invalidateQueries({ queryKey: ['solHistory', address] });
      queryClient.invalidateQueries({ queryKey: ['points', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['points', 'ledger'] });
      queryClient.invalidateQueries({ queryKey: ['userPoints', address] });
      queryClient.invalidateQueries({ queryKey: ['gateCampaign'] });

      if (data.swappedTokens > 0 || data.burnedAssets > 0) {
        const parts: string[] = [];
        if (data.swappedTokens > 0) {
          parts.push(`Swapped ${data.swappedTokens} token${data.swappedTokens > 1 ? 's' : ''}`);
        }
        if (data.burnedAssets > 0) {
          parts.push(`Burned ${data.burnedAssets} asset${data.burnedAssets > 1 ? 's' : ''}`);
        }

        toast.success('Junk complete!', {
          description: parts.join(' and '),
        });
      }
    },
    onError: (error: Error) => {
      console.error('[useCombinedJunk] Error:', error);

      captureException(error, {
        operation: 'combined_junk',
        errorMessage: error.message,
      });

      // Parse the error for a user-friendly message
      const parsedError = parseSolanaError(error);

      // Don't show toast for user cancellations
      if (error.message?.includes('User rejected') || error.message?.includes('cancelled')) {
        return;
      }

      toast.error(parsedError.isRetryable ? 'Please try again' : 'Operation failed', {
        description: parsedError.suggestedAction || parsedError.message,
      });
    },
  });
}
